/**
 * 业务目录数据（来自参考站逆向产物，非编造）
 * 来源：电商工作台-参考站检阅/逆向产物/{models,banner-options,feature-prompts}.json
 */
import { FALLBACK_FAMILIES } from './modelFamilies'
import { PLATFORMS } from './platforms'

export type ModelKind = 'image' | 'video' | 'audio' | 'llm'
export type ModelSpec = { slug: string; label: string; kind: ModelKind; note?: string }

/**
 * 参考站原始的 31 个上游 slug。
 *
 * ⚠️ **不要用这张表渲染模型下拉**，用 `context/ModelsContext`（来自
 * `GET /api/aigc/models` 的 `families`）。原因：这 31 个里
 *   · 20 个是本项目网关/duomi 都没有的上游（Seedream / Sunburst / Topaz / Seedance / 万相）
 *   · 6 个是同一个模型的别名（gpt-image-2 被参考站拆成「官方 / 图生图 / 渠道 / 渠道编辑」）
 * 它们对参考站有效，对我们无效。
 *
 * 保留用途：给历史数据（作品库、用量账本里存过的 slug）显示中文名。
 */
export const MODELS: ModelSpec[] = [
  { slug: 'gpt-image-2', label: 'GPT Image 2 官方', kind: 'image' },
  { slug: 'gpt-image-2-image-to-image-official-stable', label: 'GPT Image 2 图生图', kind: 'image' },
  { slug: 'gpt-image-2-0-text-to-image-channel-low-price', label: 'GPT Image 2 渠道', kind: 'image', note: '低价' },
  { slug: 'gpt-image-2-0-edit-channel-low-price', label: 'GPT Image 2 渠道编辑', kind: 'image', note: '低价' },
  { slug: 'nano-banana2-gemini31flash-text-to-image-official-stable', label: 'Nano Banana 2 官方', kind: 'image' },
  { slug: 'nano-banana2-gemini31flash-image-to-image-official-stable', label: 'Nano Banana 2 图生图', kind: 'image' },
  { slug: 'seedream-v5-pro-text-to-image', label: 'Seedream 5 Pro', kind: 'image' },
  { slug: 'seedream-v5-pro-image-to-image', label: 'Seedream 5 Pro 图生图', kind: 'image' },
  { slug: 'gpt-image-2-5-flare-text-to-image-economy', label: 'GPT Image 2.5 Flare', kind: 'image', note: '经济' },
  { slug: 'gpt-image-2-5-flare-image-to-image-economy', label: 'GPT Image 2.5 Flare 图生图', kind: 'image', note: '经济' },
  { slug: 'gpt-image-2-5-sunburst-text-to-image-economy', label: 'GPT Image 2.5 Sunburst', kind: 'image', note: '经济' },
  { slug: 'gpt-image-2-5-sunburst-image-to-image-economy', label: 'GPT Image 2.5 Sunburst 图生图', kind: 'image', note: '经济' },
  { slug: 'topazlabs-image-upscale-standard-v2', label: '2K 放大 (Topaz)', kind: 'image' },
  { slug: 'topazlabs-image-upscale-high-fidelity-v2', label: '高清放大 (Topaz)', kind: 'image' },
  { slug: 'seedance-2', label: 'Seedance 2.0 文生视频', kind: 'video' },
  { slug: 'seedance-2.5', label: 'Seedance 2.5 文生视频', kind: 'video' },
  { slug: 'seedance-2-5-image-to-video-token', label: 'Seedance 2.5 图生视频', kind: 'video' },
  { slug: 'seedance-2-5-multimodal-video-token', label: 'Seedance 2.5 全能参考', kind: 'video' },
  { slug: 'seedance2-0-image-to-video', label: 'Seedance 2.0 图生视频', kind: 'video' },
  { slug: 'seedance2-0-multimodal-video', label: 'Seedance 2.0 全能参考', kind: 'video' },
  { slug: 'seedance2-0-fast-text-to-video', label: 'Seedance Fast 文生视频', kind: 'video', note: '快' },
  { slug: 'seedance2-0-fast-image-to-video', label: 'Seedance Fast 图生视频', kind: 'video', note: '快' },
  { slug: 'seedance2-0-fast-multimodal-video', label: 'Seedance Fast 全能参考', kind: 'video', note: '快' },
  { slug: 'seedance-2-0-mini-image-to-video', label: 'Seedance Mini 图生视频', kind: 'video', note: '省' },
  { slug: 'minimax-h3-text-to-video', label: 'MiniMax H3 文生视频', kind: 'video' },
  { slug: 'minimax-h3-image-to-video-first-last-frame', label: 'MiniMax H3 图生视频', kind: 'video' },
  { slug: 'minimax-h3-multimodal-reference-to-video', label: 'MiniMax H3 全能参考', kind: 'video' },
  { slug: 'wan-3-0-image-to-video', label: '万相 3.0 图生视频', kind: 'video' },
  { slug: 'wan-3-0-reference-to-video', label: '万相 3.0 参考生视频', kind: 'video' },
  { slug: 'wan-3-0-prime-image-to-video', label: '万相 3.0 高速版 图生视频', kind: 'video' },
  { slug: 'wan-3-0-prime-reference-to-video', label: '万相 3.0 高速版 参考生视频', kind: 'video' },
]

export const modelsOf = (kind: ModelKind) => MODELS.filter((m) => m.kind === kind)

/**
 * slug / family id → 中文显示名。
 * 优先查真实模型族（当前在用的模型都在这里），再回落到参考站 slug 表（历史数据）。
 */
export const modelLabel = (slug: string) => {
  for (const f of FALLBACK_FAMILIES) {
    if (f.id === slug || f.textSlug === slug || f.imageSlug === slug) return f.name
  }
  return MODELS.find((m) => m.slug === slug)?.label ?? slug
}

/* -------------------------------------------------------------------------- */
/* Banner 目录                                                                 */
/* -------------------------------------------------------------------------- */

export type BannerPlatform = { id: string; name: string; style: string }

/**
 * Banner 的平台清单**从平台档案派生**，不再手写第二份。
 *
 * 手写两份的后果已经吃过：这里是 9 个（带一行 style）、文案页是 8 个纯字符串，
 * 后端 0 个 —— 三处对不上，还都没有语言和尺寸规则。
 * `style` 保留是为了不改 BannerStudio 的渲染，内容改成读档案的 styleKeywords。
 */
export const BANNER_PLATFORMS: BannerPlatform[] = PLATFORMS.filter((p) => p.banner).map(
  (p) => ({ id: p.id, name: p.name, style: p.styleKeywords.join('、') }),
)

/**
 * Banner 场景类型。
 *
 * 补齐依据：公司需求清单点名的「直播 / 专场 / 促销海报」三个场景，
 * 上一版只有「活动促销」一个能对应上，直播与专场完全没有类型可选，
 * 运营只能把直播间引流图塞进「活动促销」—— 出来的图不带开播时间与福利位。
 *
 * 顺序 = 表单里的排列顺序，把高频的（促销 / 直播）放前面。
 */
export const BANNER_TYPES = [
  { id: 'promo', name: '活动促销', description: '大促 / 打折 / 限时抢购' },
  { id: 'live', name: '直播预告', description: '直播间引流，突出开播时间与专属福利' },
  { id: 'special', name: '专场活动', description: '品牌日 / 品类专场 / 超品日，单一主题贯穿' },
  { id: 'coupon', name: '优惠券图', description: '券面额 + 领取入口，转化导向，留出可点击区域' },
  { id: 'shop-hero', name: '店铺首图', description: '品牌调性，吸引进店' },
  { id: 'festival', name: '节日特惠', description: '节日氛围，礼盒 / 送礼' },
  { id: 'new', name: '新品上市', description: '新品发布，突出卖点' },
  { id: 'wholesale', name: '批发供货', description: 'B2B 批发供货 Banner' },
  { id: 'recruit', name: '招商采购', description: '招商合作 / 采购对接' },
]

export type BannerSize = { id: string; name: string; ratio: string; w: number; h: number }

export const BANNER_SIZES: BannerSize[] = [
  { id: 'pc-hero', name: 'PC 首焦 / 直播间', ratio: '16:9', w: 1920, h: 1080 },
  { id: 'shop-banner', name: '店铺横幅', ratio: '16:9', w: 1920, h: 1080 },
  { id: 'wide', name: '超宽通栏', ratio: '16:9', w: 2560, h: 720 },
  { id: 'mobile-push', name: '移动端推送', ratio: '16:9', w: 1200, h: 628 },
  { id: 'square', name: '方形通用', ratio: '1:1', w: 1200, h: 1200 },
  { id: 'poster', name: '竖版海报', ratio: '3:4', w: 1200, h: 1600 },
  { id: 'short-video', name: '短视频竖屏', ratio: '9:16', w: 1080, h: 1920 },
]

/* -------------------------------------------------------------------------- */
/* 专业工作流（专业服饰 9 个 / 全品类商品 6 个）                                */
/* -------------------------------------------------------------------------- */

/*
 * 2026-10-10 删掉「专业服饰」下的**模特生成**档（id `model-gen`）。
 *
 * 用户口径：截图圈着下拉里这一项「这个就不要了，重复了」。
 * 它确实与全品类那档 `product-model`（**AI 模特图**）重复 —— 都是「按描述定制模特」，
 * 而 AI 模特图本就设计成**跨品类**的（见 `PRODUCT_FEATURES` 里那段注释：
 * 模特不属于任何单一素材类），卖水杯的也能用。两档并存的结果是同一个能力
 * 在「专业服饰」和「全品类商品」两个下拉里各叫一个名字，用户要自己想清楚点哪个。
 *
 * 连带动作（一处删、三处跟着走，改的时候别漏）：
 *   · 提示词模板 `fashion.model-gen` 一并从 `prompt-templates.json` 删除（成了孤儿）；
 *   · 首页模板卡 `st-03「虚拟模特穿搭图生成」` 删掉（它的深链就是 `?feature=model-gen`，
 *     档没了这张卡会静默落到别的档）；
 *   · `fashion-model-gen` 这个**旧页面键**仍留在 `pageKeys.ts` 的归一表里 ——
 *     库里还有以它落库的历史任务，删了那批记录右栏就整批查不出来（见该文件）。
 */

/**
 * 一个「专业工作流」的完整定义。
 *
 * 上一版把 `required` 写死在页面里（`const needPrimary = true`），
 * 结果「模特生成」的服装图明明标着「可选」，提交按钮却一直灰着。
 * 必填/可选/数量上限全部归到数据里，页面只负责读，不再自己判断。
 */
export type StudioFeature = {
  id: string
  name: string
  description: string
  /** 提示词模板 id，见 config/prompt-templates.json */
  template: string
  /** 'fashion' 服饰 / 'product' 全品类商品 —— 决定套图结构与视觉语言 */
  family: 'fashion' | 'product'

  // ---- 输入 ----
  /**
   * 参考图那一段的标签。**可以不声明** —— 不声明 = 本档根本没有参考图
   * （配合 `textOnly`，见「AI 模特图」：纯文字出图，上传区整块不渲染）。
   * 三个字段要么一起给，要么一起不给：只剩 `primaryMax` 会让上传框少个标题。
   */
  primaryLabel?: string
  /** 主图是否必填。false 时页面不显示必填星号，也不拦提交 */
  primaryRequired?: boolean
  primaryMax?: number
  /**
   * 参考图**不由用户上传**，而是从同页另一个工作流的产出里挑一张。
   * 填的是来源工作流的 `id`（同页内，如 `product-model`）。
   *
   * 「场景图」2026-09-29 用的就是它（用户口径：「场景图不需要上传商品组件」），
   * 但 2026-09-30 起场景图改成 `scenePlan` —— 候选放宽到**本页全部档**的产出
   * （白底主图 / 多角度 / 细节特写都算），指死「AI 模特图」那一档反而会
   * 「有图却选不到」。**目前没有档位在用**，机制留着（`RefUploader` 的 `source`）。
   *
   * 填了它：该档**不渲染上传区**，改渲染候选条（候选见 `refCandidatesFor`，
   * 取数含后端历史 —— 上一轮跑出来的图刷新页面后还得挑得到）。
   */
  primaryFrom?: string
  secondaryLabel?: string
  secondaryRequired?: boolean
  secondaryMax?: number
  textLabel?: string
  textRequired?: boolean
  textPlaceholder?: string
  textHint?: string
  /** 纯文字即可出图 —— 这一档**整块不渲染参考图上传区**（目前的档：AI 模特图 / 场景图） */
  textOnly?: boolean
  /**
   * 这一档要一份**结构化的模特设定**（目标市场 / 性别 / 年龄 / 体型 / 气质 / 发型 / 姿势 / 场景）。
   *
   * 只有「AI 模特图」用：上一版这里只有一个自由文本框，
   * 用户填「东亚女性自然手持」这种话要自己想措辞，选项也记不住 ——
   * 2026-09-30 用户给的对照表把这件事定成了可点的选择器。
   * 选项真源见 `config/modelProfile.ts`，页面只按这个开关决定渲不渲染。
   */
  modelLook?: boolean
  /**
   * 这一档要一份**结构化的场景设定**（场景类型 + 居家二级空间 / 场景风格）。
   *
   * 目前只有「场景图」用（2026-09-30 用户给的配置表）：上一版这里是一个
   * 「场景倾向（可选）」自由文本框，用户得自己想措辞、选项也记不住。
   * 选项真源见 `config/sceneProfile.ts`，页面只按这个开关决定渲不渲染。
   *
   * ⚠️ 它还兼着一件事：**这一档同时不出上传区**，参考图从本页已有的产出里点选
   * （2026-09-30 用户口径「商品什么鬼去掉啊，这个是场景图」—— 与 09-29 那条
   * 「场景图不需要上传商品组件」是同一条）。所以页面上别再加回上传框，
   * 候选条就是这一档唯一的参考图入口，见 `FashionStudio` 的 `pickSource`。
   */
  scenePlan?: boolean
  /** 画面允许出现营销文字（卖点图这类）。默认 false —— 电商图里多一个错字就是事故 */
  textInImage?: boolean

  // ---- 输出 ----
  outputs: number
  outputOptions: number[]
  ratioDefault: string
  ratioOptions: { value: string; label: string }[]
  resolutionDefault: '1k' | '2k' | '4k'
  estimatedMs: number
}

const APPAREL_RATIOS = [
  { value: '3:4', label: '3:4 服饰' },
  { value: '9:16', label: '9:16 竖版' },
  { value: '1:1', label: '1:1 主图' },
]

const PRODUCT_RATIOS = [
  { value: '1:1', label: '1:1 主图' },
  { value: '3:4', label: '3:4 竖版' },
  { value: '16:9', label: '16:9 横版' },
]

export const FASHION_FEATURES: StudioFeature[] = [
  {
    id: 'clothing-on',
    name: '服装上身',
    description: '上传服装图 + 可选人物图，模特真实上身展示',
    template: 'fashion.clothing-on',
    family: 'fashion',
    primaryLabel: '服装图',
    primaryRequired: true,
    primaryMax: 3,
    secondaryLabel: '人物图（可选）',
    secondaryMax: 1,
    textLabel: '补充要求（可选）',
    textPlaceholder: '如：亚洲女模特、街拍场景、暖调自然光',
    textHint: '这段文字会作为「补充要求」拼进提示词',
    outputs: 4,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '3:4',
    ratioOptions: APPAREL_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 45000,
  },
  {
    id: 'face-swap',
    name: '模特换脸',
    description: '替换模特脸部，保留身体姿态与背景',
    template: 'fashion.face-swap',
    family: 'fashion',
    primaryLabel: '模特图',
    primaryRequired: true,
    primaryMax: 1,
    secondaryLabel: '人脸参考图',
    secondaryRequired: true,
    secondaryMax: 1,
    outputs: 3,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '3:4',
    ratioOptions: APPAREL_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 45000,
  },
  {
    id: 'bg-swap',
    name: 'AI 换背景',
    description: '替换图片背景，保留主体不变',
    template: 'fashion.bg-swap',
    family: 'fashion',
    primaryLabel: '主体图',
    primaryRequired: true,
    primaryMax: 1,
    textLabel: '背景描述',
    textRequired: true,
    textPlaceholder: '如：阳光明媚的海滩、暖色调、浅景深',
    textHint: '背景写具体（地点 + 光线 + 色调），比写「好看一点」有效得多',
    outputs: 4,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '3:4',
    ratioOptions: APPAREL_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 45000,
  },
  {
    id: 'remove-bg',
    name: 'AI 去背景',
    description: '服装图一键白底，用于平台白底主图',
    template: 'fashion.remove-bg',
    family: 'fashion',
    primaryLabel: '服装图',
    primaryRequired: true,
    primaryMax: 1,
    outputs: 1,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '3:4',
    ratioOptions: APPAREL_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 20000,
  },
  {
    id: 'variations',
    name: '套图裂变',
    description: '单张成片裂变多角度 / 多场景变体',
    template: 'fashion.variations',
    family: 'fashion',
    primaryLabel: '成片图',
    primaryRequired: true,
    primaryMax: 1,
    textLabel: '变体方向（可选）',
    textPlaceholder: '如：换户外场景、拉近成半身特写',
    textHint: '不填就按「角度 / 场景 / 光线 / 裁切」四个维度自动铺开',
    // 2026-10-10 用户口径「出图数量 1-4」：默认张数收敛到 4（上一版 6）。
    // 想要更多直接敲张数框右侧那个数字（自定义数量），不再被锁在预设上。
    outputs: 4,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '3:4',
    ratioOptions: APPAREL_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 60000,
  },
  {
    id: 'garment-flat-lay',
    name: '提取白底平铺图',
    description: '服装正背面白底平铺展示',
    template: 'fashion.garment-flat-lay',
    family: 'fashion',
    primaryLabel: '服装图',
    primaryRequired: true,
    primaryMax: 3,
    outputs: 2,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '3:4',
    ratioOptions: APPAREL_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 30000,
  },
  {
    id: 'garment-3d',
    name: '服装立体图',
    description: '无模特无衣架的白底立体展示',
    template: 'fashion.garment-3d',
    family: 'fashion',
    primaryLabel: '服装图',
    primaryRequired: true,
    primaryMax: 2,
    outputs: 2,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '3:4',
    ratioOptions: APPAREL_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 30000,
  },
  {
    id: 'garment-hang',
    name: '服装挂拍',
    description: '衣架挂拍正背面，比平铺更接近实物垂坠感',
    template: 'fashion.garment-hang',
    family: 'fashion',
    primaryLabel: '服装图',
    primaryRequired: true,
    primaryMax: 3,
    textLabel: '补充要求（可选）',
    textPlaceholder: '如：木色衣架、白墙背景、正面 + 背面各一张',
    outputs: 2,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '3:4',
    ratioOptions: APPAREL_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 30000,
  },
  {
    id: 'garment-detail',
    name: '服饰细节',
    description: '面料 / 走线 / 五金 / 领口的微距特写',
    template: 'fashion.garment-detail',
    family: 'fashion',
    primaryLabel: '服装图',
    primaryRequired: true,
    primaryMax: 3,
    textLabel: '重点部位（可选）',
    textPlaceholder: '如：领口走线、袖口罗纹、金属拉链、面料纹理',
    textHint: '写几个部位就出几张图（最多 6 张），不填按「面料 / 走线 / 五金 / 版型」铺开',
    outputs: 4,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '3:4',
    ratioOptions: APPAREL_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 45000,
  },
]

/**
 * 全品类商品工作流。
 *
 * 上一版这里是个死链：点「全品类商品 →」跳到 /professional/products，
 * 页面却仍渲染服饰那 8 个 feature，换个徽标而已。
 * 现在按非服饰品类的真实需求另立一套：白底主图 → 场景 → 卖点 → 多角度 → 细节 → 包装，
 * 覆盖一个商品详情页从头到尾需要的图。
 */
export const PRODUCT_FEATURES: StudioFeature[] = [
  {
    id: 'product-main',
    name: '白底主图',
    description: '纯白背景首图，搜索位一眼看清产品',
    template: 'product.product-main',
    family: 'product',
    primaryLabel: '商品图',
    primaryRequired: true,
    primaryMax: 3,
    textLabel: '补充要求（可选）',
    textPlaceholder: '如：突出金属质感、45 度俯视',
    // 需求口径是「电商主图一次 4 张」—— 上一版默认 1 张，
    // 用户要凑满一整套主图得手动点四次，默认值就该等于最常见的交付量。
    outputs: 4,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '1:1',
    ratioOptions: PRODUCT_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 30000,
  },
  {
    // 公司清单「四、模特 / 数字人」第 1 条：AI 模特图（主图 / 详情页可选「是否添加模特」）。
    // 注意它不是服饰专用的 —— 上一版把「模特生成」只挂在专业服饰下，
    // 结果是卖水杯的想加个手持模特，得先点进「专业服饰」，入口品类全错。
    id: 'product-model',
    name: 'AI 模特图',
    description: '按模特与场景要求生成真人模特素材，主图与详情页按需开关「是否添加模特」',
    template: 'product.product-model',
    family: 'product',
    /**
     * ⚠️ **这一档没有参考图**（2026-09-30 用户第二次口径：「AI 模特跟商品图有毛关系」）。
     *
     * 上一版逼着用户先传 1~3 张商品图，界面第一屏是一排上传框 ——
     * 与「模特与场景要求 → 生成 AI 模特图」这件事毫无关系，纯属挡路。
     * 现在**不声明 `primaryLabel` / `primaryRequired`**，并显式 `textOnly`，
     * 上传区整块不渲染（见 `FashionStudio` 的 `stepAssets`）。
     * 别在这里「顺手」把商品图加回来：要出「产品 + 模特」请走商品图那一档的
     * 「是否添加模特」，或本页的「场景图」。
     *
     * 输入 = **模特与场景要求**（模特设置 / 外貌特征 / 姿势与场景 / 补充要求）。
     * 前四组是选择器（真源 `config/modelProfile.ts`），这里只剩最后那块自由文本。
     */
    textOnly: true,
    modelLook: true,
    textLabel: '补充要求（可选）',
    textPlaceholder: '如：不露脸只到手部、手持产品、暖调自然光',
    textHint: '上面选不到的特殊要求写在这里，会作为「补充要求」拼进提示词',
    outputs: 2,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '1:1',
    ratioOptions: PRODUCT_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 40000,
  },
  {
    /**
     * 2026-09-30 用户口径（选项表见 `config/sceneProfile.ts`）：
     *   场景类型（选「居家」再二级到具体空间）→ 场景风格 → 场景描述（可选）→ 出图
     *
     * 三条口径，用户各说过一次，其实是同一条「这一档不做商品，只做场景素材」：
     *   1. **人相关的全部删掉**。上一版这一档的输入是「AI 模特图」的产出
     *      （`primaryFrom`），要求「把已带模特的产品放进场景」。现在场景图不出现人，
     *      要人就走上一档「AI 模特图」—— 两档产出的目的不同，别混着做。
     *   2. 2026-09-29「这商品图是AI模特图，场景图不需要上传商品组件」→ 上传区删掉。
     *   3. 2026-09-30「商品什么鬼去掉啊，这个是场景图」→ 只剩候选条；
     *      当天补一句「就是场景图这块跟ai模特一样，不需要商品图什么的」
     *      ⇒ **参考图整块不要**，与 AI 模特图同一处理（`textOnly`）。
     *
     * ⚠️ 所以这一档 `primaryLabel / primaryRequired / primaryMax` **一个都不声明**
     * （`textOnly` 的规矩：要么三件套一起给、要么一起不给）——只把标签改成
     * 「可选」而留一个上传框，等于没改。
     * 提示词模板 `product.product-scene` 的【输入说明】写的是同一件事，一起改。
     */
    id: 'product-scene',
    name: '场景图',
    description: '将产品自然融入真实使用场景，快速生成电商与广告场景图',
    template: 'product.product-scene',
    family: 'product',
    textOnly: true,
    scenePlan: true,
    textLabel: '场景描述（可选）',
    textPlaceholder: '例如：现代高端客厅，大面积落地窗，午后自然光，产品作为视觉主体',
    textHint: '上面选不到的写在这里；场景类型 / 风格选了「自定义」时，以这里为准',
    outputs: 4,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '1:1',
    ratioOptions: PRODUCT_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 40000,
  },
  {
    id: 'product-selling-point',
    name: '卖点图',
    description: '一图一个购买理由，带图示与短文案',
    template: 'product.product-selling-point',
    family: 'product',
    primaryLabel: '商品图',
    primaryRequired: true,
    primaryMax: 3,
    textLabel: '卖点内容',
    textRequired: true,
    textPlaceholder: '如：静音设计 / 可拆洗 / 三年质保\n一行一个卖点',
    textHint: '写几个卖点就出几张图（最多 6 张）。不许编造产品没有的功能',
    textInImage: true,
    outputs: 4,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '1:1',
    ratioOptions: PRODUCT_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 45000,
  },
  {
    id: 'product-multi-angle',
    name: '多角度展示',
    description: '同一套光下的多机位，讲清结构',
    template: 'product.product-multi-angle',
    family: 'product',
    primaryLabel: '商品图',
    primaryRequired: true,
    primaryMax: 3,
    textLabel: '补充要求（可选）',
    textPlaceholder: '如：需要一张底部视角，展示防滑垫',
    outputs: 4,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '1:1',
    ratioOptions: PRODUCT_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 45000,
  },
  {
    id: 'product-detail',
    name: '细节特写',
    description: '微距讲做工与材质，替代到店上手',
    template: 'product.product-detail',
    family: 'product',
    primaryLabel: '商品图',
    primaryRequired: true,
    primaryMax: 3,
    textLabel: '重点细节（可选）',
    textPlaceholder: '如：接缝走线、按键阻尼、接口做工',
    outputs: 3,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '1:1',
    ratioOptions: PRODUCT_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 45000,
  },
  {
    id: 'product-packaging',
    name: '包装礼盒',
    description: '包装形态与开箱陈列，服务礼赠场景',
    template: 'product.product-packaging',
    family: 'product',
    primaryLabel: '商品图 / 包装图',
    primaryRequired: true,
    primaryMax: 2,
    textLabel: '补充要求（可选）',
    textPlaceholder: '如：展示开盒内衬与配件排列',
    textInImage: true,
    outputs: 2,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '1:1',
    ratioOptions: PRODUCT_RATIOS,
    resolutionDefault: '2k',
    estimatedMs: 30000,
  },
  {
    id: 'product-booth',
    name: '展台背景图',
    description: '商品置于展台 / 背板前的净底展示，用于直播与展会背景',
    template: 'product.product-booth',
    family: 'product',
    primaryLabel: '商品图',
    primaryRequired: true,
    primaryMax: 3,
    textLabel: '展台风格（可选）',
    textPlaceholder: '如：大理石台面 + 渐变背板、品牌名放顶部居中',
    textHint: '留出主播站位与画面安全区；背板文字会被真实渲染，别写没确认过的品牌信息',
    textInImage: true,
    outputs: 2,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '16:9',
    ratioOptions: [
      { value: '16:9', label: '16:9 直播背景' },
      { value: '9:16', label: '9:16 竖版展台' },
      { value: '1:1', label: '1:1 方图' },
    ],
    resolutionDefault: '2k',
    estimatedMs: 45000,
  },
]

/** 路由 category → 特征集。'fashion' 与 'products' 是参考站的 IA 命名，保持兼容。 */
export const FEATURE_SETS: Record<string, StudioFeature[]> = {
  fashion: FASHION_FEATURES,
  products: PRODUCT_FEATURES,
}

/**
 * 类目显示名 —— `/professional/:category` 页上那枚类目 chip 的唯一文案来源。
 *
 * 与侧栏入口名（`nav.ts` 里叫「商品图 / 服饰图」）**刻意不同**：
 * 侧栏名说的是「这是什么页面」，这里说的是「这一档覆盖什么货」，
 * 两处含义不同所以不合并；但**同一个意思只许写一遍** ——
 * 页面里再抄一份字面量，改文案时必漏一处。
 */
export const CATEGORY_LABELS: Record<string, string> = {
  fashion: '专业服饰',
  products: '全品类商品',
}

/**
 * 图片分辨率档位 —— 服饰图页与社媒图页共用一份。
 *
 * 原先这两页里各写一遍 `[{value:'1k'…},{value:'2k'…},{value:'4k'…}]`，
 * 属于「同一份清单两处各写一遍」；档位一旦要加 8K 或改名，必然漏掉一页。
 */
export const IMAGE_RESOLUTIONS: { value: string; label: string }[] = [
  { value: '1k', label: '1K' },
  { value: '2k', label: '2K' },
  { value: '4k', label: '4K' },
]

/**
 * 图片比例的**兜底清单** —— 后端连不上（mock / 离线 / 刚启动还没拉到）时用。
 *
 * 真源在后端：`GET /api/aigc/models` 的 `families[].capabilities.aspect_ratios`，
 * **按模型所属上游分别下发**。这里抄的是 duomi 直连那份
 * （`HW_IMAGE_BACKEND` 的默认值就是 duomi），网关那份（Nano Banana 全系）
 * 只有 6 档、没有 `1:2 / 5:4 / 1.91:1 / 2:1 / 21:9`，**由后端下发，前端不维护第二份**。
 *
 * 顺序与后端 `backend/app/image_sizes.py` 的 `DUOMI_IMAGE_SIZES` 逐字一致：
 * 方图 → 竖版（宽到窄）→ 横版（窄到宽）。改一处必须同步另一处，
 * 冒烟脚本 `smoke-detail-specs` 会比对这两个文件。
 *
 * ⚠️ 加比例的唯一合法路径：**先在 `image_sizes.py` 里实测收录**（拿真实像素
 * 打一发、下载产物读宽高确认没被降级），再往这里加。反过来做就是上一轮那个事故：
 * 前端加了 `4:5` 而后端映射表里没有，用户选了 4:5 到手一张方图，不报错、照样扣费。
 *
 * 为什么放这里而不是 `modelFamilies.ts` 旁边方便 import：那个方向会成环 ——
 * `catalog.ts` 本来就 import 了 `modelFamilies.ts`（取 FALLBACK_FAMILIES），
 * 反过来再 import 回来就是循环依赖。
 */
export const IMAGE_ASPECT_RATIOS: string[] = [
  '1:1',
  '4:5',
  '3:4',
  '2:3',
  '9:16',
  '1:2',
  '5:4',
  '4:3',
  '3:2',
  '16:9',
  '1.91:1',
  '2:1',
  '21:9',
]

/**
 * 比例的用途说明 —— 选择器下方的提示。
 *
 * 为什么要写：13 个比例光看数字没法选。「4:5 还是 3:4？」这种问题
 * 只有懂 IG 信息流和 Ozon 详情页尺寸差别的人答得上来，
 * 而用户要的是「我要发 IG 的竖版帖」→ 直接指到 4:5。
 *
 * 只收录**有明确平台归属**的档位；`2:1` / `21:9` 这种通用宽幅写通用说明，
 * 不硬编一个平台上去（编错了比不说更误导）。
 */
export const RATIO_USAGE: Record<string, string> = {
  '1:1': '主图 · 全平台通用',
  '4:5': '社媒竖版 · IG / FB 信息流',
  '3:4': '详情竖图 · Ozon / 淘宝详情',
  '2:3': '长竖图 · Pinterest',
  '9:16': '短视频封面 · Story',
  '1:2': '超长竖图 · 长图拼接',
  '5:4': '传统摄影横版',
  '4:3': '场景横图 · 演示稿',
  '3:2': '摄影横版 · 单反原生',
  '16:9': '横幅 · A+ 宽图 · 视频封面',
  '1.91:1': '信息流横图 · FB / LinkedIn',
  '2:1': '宽横幅',
  '21:9': '超宽 banner',
}

/** 比例的界面写法：带用途就写成 `4:5 · 社媒竖版 · IG / FB 信息流` */
export const ratioLabel = (ratio: string): string =>
  RATIO_USAGE[ratio] ? `${ratio} · ${RATIO_USAGE[ratio]}` : ratio

export function featuresOf(category?: string): StudioFeature[] {
  return FEATURE_SETS[category ?? 'fashion'] ?? FASHION_FEATURES
}

export function findFeature(category: string | undefined, id: string | null | undefined): StudioFeature {
  const list = featuresOf(category)
  return list.find((f) => f.id === id) ?? list[0]
}

/** 兼容旧引用：默认（服饰）特征集 */
export type FashionFeature = StudioFeature

/* -------------------------------------------------------------------------- */
/* 图片工具箱（6 个）                                                          */
/* -------------------------------------------------------------------------- */

/**
 * 2026-10-05 用户看过 12 个工具的界面后原话：**「这个不需要这么多功能」**。
 * 砍的依据分三类，都不是拍脑袋：
 *
 *   · **死的**（必须删）——「2K 放大」与「高清放大」的预设模型是
 *     `topazlabs-image-upscale-{standard,high-fidelity}-v2`，而后端 `registry.py`
 *     把两条都写成 `_na(...)`（原话：「网关未接入 Topaz 放大」）。
 *     这两个工具点下去**没有可用的上游**。合并成一个「超清放大」，
 *     改挂真实能跑的模型（见 `IMAGE_TOOL_MODELS`）。
 *
 *   · **重复的**——「AI 洗脸」（肤质 / 气色）是「精修」的一个子集，
 *     两个入口做同一件事，用户得先猜哪个更"轻"。并入「精修」。
 *
 *   · **与本页能力不符的**——「添加水印」写着「批量加店标」、
 *     「色彩调整」写着「批量化校色」，但本页 `needImage: 1`、上传框 `max={1}`，
 *     **一次只收一张图，批量在结构上就做不到**。文案与能力对不上，
 *     改文案不如删工具（真要批量得走批量出图页）。
 *     「局部重绘」需要涂抹选区，本页没有选区工具，一直是靠提示词「描述」位置，
 *     名不副实；「翻译本地化」按平台走 `localize-*` 那一组，本页重复。
 *
 * 删掉的 id：`upscale-2k` / `upscale-hq` / `face-cleanup` / `inpaint` /
 * `add-watermark` / `translate` / `color-grade`。**不要再加回来** ——
 * `smoke-img-tools.ts` 钉了「只能是这 6 个 id」。
 */
export type ImageTool = {
  id: string
  name: string
  description: string
  promptLabel?: string
  promptPlaceholder?: string
  needImage: number
  outputs: number
  estimatedMs: number
  free?: boolean
}

export const IMAGE_TOOLS: ImageTool[] = [
  { id: 'remove-bg', name: 'AI 去背景', description: '主体抠出，输出透明底 PNG', needImage: 1, outputs: 1, estimatedMs: 20000 },
  {
    id: 'bg-swap',
    name: '换背景',
    description: '保留主体与原有光影，替换成指定背景（与「去背景」是两件事：那个出透明底，这个出成片）',
    promptLabel: '目标背景',
    promptPlaceholder: '如：浅灰水泥墙 / 原木桌面 / 纯白影棚',
    needImage: 1,
    outputs: 2,
    estimatedMs: 40000,
  },
  {
    id: 'upscale',
    name: '超清放大',
    /* 两个放大合一（原「2K 放大」+「高清放大」）——它们指向同一个
       `_na` 的 Topaz 档，是同一个死掉的能力写了两遍文案。 */
    description: '把原图放大到更高分辨率，保结构、还原细节纹理',
    needImage: 1,
    outputs: 1,
    estimatedMs: 35000,
  },
  { id: 'expand', name: '智能扩图', description: '向四周扩展画布并补全内容', needImage: 1, promptLabel: '扩展方向说明', promptPlaceholder: '如：向左右各扩展 30%', outputs: 1, estimatedMs: 40000 },
  {
    id: 'retouch',
    name: '精修',
    description: '商业级精修：产品去瑕疵、人像肤质与气色，保留材质质感',
    needImage: 1,
    outputs: 2,
    estimatedMs: 40000,
  },
  { id: 'remove-watermark', name: '去水印 / 文字', description: '自动识别并抹除叠加文字', needImage: 1, outputs: 1, estimatedMs: 25000 },
]

/**
 * 工具 → 预设模型。值必须是**前端下拉里真实存在的 family id**
 * （`modelFamilies.ts` 的 `id` / 后端 `registry.FAMILIES[].id`），
 * **不许填上游 slug**。
 *
 * 为什么这条要写死在这里：2026-10-05 之前这份映射在 `ImageTools.tsx` 里，
 * 值填的是 slug（`gpt-image-2-0-edit-channel-low-price`、
 * `topazlabs-image-upscale-high-fidelity-v2`），而 `ModelSelect` 的 `value`
 * 要的是 family id —— 两边对不上，后果有两层，**两层都不报错**：
 *
 *   1. 进第二步时 `options.find(o => o.id === value)` 匹配不上，被
 *      `ModelSelect` 的兜底 effect 改写成下拉第一项 ——
 *      也就是说「工具已预设推荐模型」这句话**从来没成立过**；
 *   2. 在改写发生之前，报价已经拿那个 slug 问过后端，于是用户看到
 *      「模型「topazlabs-image-upscale-high-fidelity-v2」不可用，
 *      已按 GPT Image 2 官方计价」—— 一条**关于不存在模型的告警**。
 *
 * 六个工具**一个都不能少**：旧写法是 `TOOL_MODEL[id] ?? form.model`，
 * 没有预设的工具会**沿用上一个工具残留的模型**（点开「智能扩图」，
 * 下拉里还显示上一个「高清放大」的模型）。
 *
 * `upscale` 挂 `nano-banana-2` 是照抄后端自己的建议 —— `registry.py` 里
 * 那两条 `_na` 的 `suggest` 写的就是「可用 nano-banana 图生图 +
 * resolution=2K 近似，但效果不同」，本项目不自己发明上游能力。
 */
export const IMAGE_TOOL_MODELS: Record<string, string> = {
  'remove-bg': 'gpt-image-2.5',
  'bg-swap': 'gpt-image-2.5',
  upscale: 'nano-banana-2',
  expand: 'gpt-image-2.5',
  retouch: 'gpt-image-2.5',
  'remove-watermark': 'gpt-image-2.5',
}

/** 本页可选的分辨率 —— 报价系数真源在后端 `pricing.RESOLUTION_MULTIPLIER` */
export const IMAGE_TOOL_RESOLUTIONS = ['1k', '2k', '4k'] as const

/* -------------------------------------------------------------------------- */
/* 文案渠道                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * 文案渠道。
 *
 * 顺序 = 公司设计稿的六个子类顺序（商品 → 社媒 → 广告 → 视频 → SEO → 翻译本地化），
 * 与侧栏「② 文案」的排列一一对应，用户不用在两套顺序之间做映射。
 *
 * 上一版把「产品卖点提炼 / 详情页文案 / 主图卖点关键词」三项全塞进 product 的
 * 一句话描述里（"主图卖点 / 详情页文案 / A+ 模块"），等于三项需求共用一个入口 ——
 * 用户点进「商品文案」拿到的是一锅炖，既不是纯卖点清单，也不是能直接上详情的成稿。
 * 现在按公司清单拆成独立渠道，各自有明确的产出形态与硬约束。
 *
 * 拆的时候有一条硬规则：**产出形态相同的不许拆**。
 * 设计稿里「广告标题 / 广告正文 / 促销文案 / CTA」是四行，但它们是同一条广告的四个段落，
 * 拆成四个入口的结果是用户要跑四遍、还得自己拼起来 —— 合成 ad-copy 一次出齐。
 * 同理「关键词 / 产品描述 / 平台关键词」合成 seo，「多语言 / 跨境 / 国家版本」合成 localization。
 */
export type CopyGroup = 'product' | 'social' | 'ad' | 'video' | 'seo' | 'localize'

/**
 * 文案子类。侧栏「② 文案」下的 6 个入口就是这 6 个 id ——
 * 页面按子类过滤渠道，所以侧栏点「广告文案」进来看到的是广告那几条，
 * 不是 14 条渠道铺一屏。
 */
export const COPY_GROUPS: { id: CopyGroup; name: string; description: string }[] = [
  { id: 'product', name: '商品文案', description: '卖点提炼 / 标题 / 详情页 / 主图关键词' },
  { id: 'social', name: '社媒文案', description: '小红书种草 / 朋友圈 / IG · FB · TikTok' },
  { id: 'ad', name: '广告文案', description: '广告标题 / 正文 / 促销文案 / CTA' },
  { id: 'video', name: '视频文案', description: '直播口播稿 / 带货口播 / 短视频脚本与分镜' },
  { id: 'seo', name: 'SEO', description: 'SEO 标题 / 关键词 / 产品描述 / 平台关键词' },
  { id: 'localize', name: '翻译本地化', description: '多语言翻译 / 跨境本地化 / 不同国家版本' },
]

export type CopyChannel = {
  id: string
  name: string
  description: string
  /** 归属子类 —— 侧栏入口与页面 chips 都按它分组，真源只有这一处 */
  group: CopyGroup
  /**
   * 这个渠道**天然的投放平台**（`PLATFORMS[].id`，见 `config/platforms.ts`）。
   *
   * 只给「名字本身就是平台 / 市场」的渠道填 —— 小红书种草、朋友圈、IG·FB·TikTok、
   * 抖音口播。选中这类渠道时，页面会把投放平台一起切过来，让
   * **平台档案（语言 / 风格 / 禁词）与渠道语义对齐**。
   *
   * ⚠️ 它存在的直接原因（2026-10-10，用户原话「fb的文案怎么是中文的」）：
   * 渠道「Instagram / Facebook / TikTok 文案」的意图里写着「英文正文」，但投放平台
   * 还停在「小红书」，于是同一份提示词里出现两条互相打架的语言指令 ——
   *   `生成 Instagram / Facebook / TikTok 文案：英文正文…`
   *   `【写作用语】中文（简体） —— 全文必须使用该语言，不要中英夹杂`
   * 模型听的是后者，于是 FB 文案出成了中文。**不是模型不听话，是指令自相矛盾。**
   *
   * 而文案渠道与投放平台本来就是两次独立的选择（不像 `/social-image` 那样渠道直接
   * 绑死平台档案），所以「换渠道 = 换工种」这句话在语言这一层没有落地。这里补上。
   *
   * ⚠️ 平台无关的渠道（标题 / 详情页文案 / 主关键词 / 广告 / SEO / 翻译本地化…）
   * **不许填** —— 它们要对哪个平台出稿是真决策，得由用户自己选，
   * 填了就等于替他做了决定，还会把「给 Amazon 写标题」这条路堵死。
   *
   * 取值必须真实存在于 `PLATFORMS`，由 `scripts/smoke-copy-language.ts` 钉住
   * （类型系统只能保证「填了个字符串」）。
   */
  targetPlatform?: string
}

export const COPY_CHANNELS: CopyChannel[] = [
  // —— 商品文案 ——
  { id: 'selling-point', group: 'product', name: '产品卖点提炼', description: '从产品资料里挖卖点，按「用户痛点 → 产品事实」成对输出，不带修饰词' },
  { id: 'title', group: 'product', name: '商品标题 / SEO 标题', description: '按平台规则生成高点击标题，附词序、属性词数量与违规词诊断' },
  { id: 'detail-copy', group: 'product', name: '详情页文案', description: '按详情页模块顺序出稿：首屏钩子 → 核心卖点 → 参数 → 场景 → 信任背书' },
  { id: 'main-keywords', group: 'product', name: '主图卖点关键词', description: '图上用的短词，4-8 字一条，直给不绕弯，供主图与卖点图直接排版' },
  { id: 'product', group: 'product', name: '商品文案总览', description: '一稿看清全貌：核心卖点 + 详情页模块结构' },
  // —— 社媒文案 ——
  // 这三条 + 下面的抖音口播，**名字就是平台**，所以填 `targetPlatform`：
  // 选中时把投放平台一起切过去，否则会出「小红书平台 + IG 渠道」这种指令打架的稿
  // （起因见 `CopyChannel.targetPlatform` 那段注释）。
  { id: 'xhs', group: 'social', name: '小红书种草文案', description: '标题 + 正文 + 话题标签，第一人称真实体验，Inspiration-first', targetPlatform: 'xhs' },
  { id: 'moments', group: 'social', name: '朋友圈文案', description: '私域短文案，场景代入 + 行动号召，不刷屏', targetPlatform: 'moments' },
  {
    id: 'social-intl',
    group: 'social',
    name: 'Instagram / Facebook / TikTok 文案',
    description: '海外社媒图文：英文短句 + hashtag 组合，先说场景不讲参数，拒绝中文直译腔',
    /*
     * 绑 `instagram` 平台档案（`copyLanguage: '英语'`）。
     * ⚠️ 选它而不是 `tiktok-shop` / `amazon-us`：这条渠道名里 IG / FB 在前，
     * 且它服务的正是「图文流」——IG / FB 档案的卖点框架是 scene-story
     * （生活场景 > 情绪共鸣 > 产品如何融入 > 轻量行动号召），与渠道描述逐条对上。
     * 换平台仍可在下拉里改（要投 TikTok 就自己切到 TikTok Shop 档案）。
     */
    targetPlatform: 'instagram',
  },
  // —— 广告文案 ——
  {
    id: 'ad-copy',
    group: 'ad',
    name: '广告文案（标题 · 正文 · 促销 · CTA）',
    description: '一条广告一次出齐：标题给点击、正文给理由、促销给时限、CTA 给动作，四段互不重复',
  },
  // —— 视频文案 ——
  {
    id: 'live-script',
    group: 'video',
    name: '直播口播稿',
    description: '开场留人 → 产品演示 → 卖点讲解 → 异议处理 → 逼单，标注时间轴与互动话术',
  },
  { id: 'douyin', group: 'video', name: '带货口播稿', description: '3 秒钩子 + 一口一段的口播稿，按口播节奏断句，不给书面语', targetPlatform: 'douyin' },
  {
    id: 'short-script',
    group: 'video',
    name: '短视频脚本 · 分镜表',
    description: '逐镜出表：镜号 / 时长 / 画面 / 台词 / 音效，拍摄方照着能直接开机',
  },
  // —— SEO ——
  {
    id: 'seo',
    group: 'seo',
    name: 'SEO 关键词 · 产品描述 · 平台关键词',
    description: '关键词分层（大词 / 精准词 / 长尾）、搜索位可用的产品描述、按平台分别给的词表',
  },
  // —— 翻译本地化 ——
  {
    id: 'localization',
    group: 'localize',
    name: '翻译本地化',
    description: '多语言翻译 / 跨境本地化 / 不同国家版本：不是逐字译，是按目标国习惯重写表达与计量单位',
  },
]

/**
 * 文案写作的默认口径（语气 / 篇幅）—— **一处真源**。
 *
 * `/copy` 的草稿默认值，与 `/social-image` 那个「零参数自动出文案」走的是同一份：
 * 社媒图页刻意不给语气 / 篇幅入口（用户 2026-10-09 选的就是「零参数，全自动」），
 * 于是它必须能**引用**这里，而不是在页面里再写一遍字符串 ——
 * 写两处的话，哪天 `/copy` 默认语气改了，社媒图配出来的文案还是老口径，
 * 而两边界面上都看不出差异（一处是下拉的初始值、一处根本没有控件）。
 */
export const COPY_WRITING_DEFAULTS = {
  tone: '真实自然，不吹',
  length: '中等（300-500 字）',
} as const

/* -------------------------------------------------------------------------- */
/* 社交种草图（小红书 / 朋友圈）                                                 */
/* -------------------------------------------------------------------------- */

/**
 * 社媒图与电商图的差别不在尺寸，在**叙事**和**文字占比**。
 *
 * 淘宝主图要「一眼看清产品」，小红书封面要「让人想点进去」——
 * 同一条素材规范套两边，结果必然是封面太像广告没人点、主图太含蓄没人买。
 * 所以这里单独建模，不复用 IMAGE_TOOLS 或商品工作流。
 *
 * 文字占比是社媒图的命门，必须写进配置而不是留在提示词里口口相传：
 * 小红书封面文字 30-50% 是平台算法与用户习惯共同决定的甜点区，
 * 电商主图那套「文字越少越好」搬过来就是没有点击率。
 */
export type SocialChannel = {
  id: string
  name: string
  description: string
  /** 按这个顺序出图，第 1 张即封面位，决定点击率 */
  structure: string[]
  outputs: number
  outputOptions: number[]
  ratioDefault: string
  ratioOptions: { value: string; label: string }[]
  /** 画面文字占比 —— 社媒图与电商图的根本分歧 */
  textRule: string
  /** 画面质感要求 */
  visualRule: string
  resolutionDefault: '1k' | '2k'
  estimatedMs: number
  /**
   * 这一档社媒图**配套文案**的渠道 id（= `COPY_CHANNELS` 的 id）。
   *
   * 🔴 社媒图（本文件上面的 `SOCIAL_CHANNELS`）与文案（`COPY_CHANNELS`）是**两套
   * 独立枚举** —— 一边是「出什么图」，一边是「写什么稿」，id 空间也不同
   * （`overseas` 图对的是 `social-intl` 文案，名字都不一样）。中间这道映射
   * 一旦某处回落兜底，症状是**静默出成别处的文案**：选「朋友圈配图」拿到
   * 小红书笔记的写法（第一人称种草 + 话题标签），钱照扣、稿照出、断言全绿。
   *
   * 所以它是**必填字段**：新增图档时漏填，`typecheck` 当场拦下。
   * 取值一律写**目标档的 id**（不是名字），并且必须真实存在于 `COPY_CHANNELS`
   * —— 后半句靠 `scripts/smoke-social-copy.ts` 钉住（类型系统只能保证
   * 「填了个字符串」，保证不了「填的是个真渠道」）。
   */
  copyChannel: string
}

const SOCIAL_RATIOS = [
  { value: '3:4', label: '3:4 竖版（小红书）' },
  { value: '4:5', label: '4:5 竖版（IG 信息流）' },
  { value: '1:1', label: '1:1 方图' },
  { value: '9:16', label: '9:16 全屏' },
]

/**
 * ⚠️ 三档的**默认张数都是 1**（2026-10-09 用户口径：「出一张图+一个文案就可以」）。
 *
 * 这一页交付的是「一条图文」——一张图 + 一份配套文案。默认就出 1 张，
 * 要铺一整套的用户再用选择器往上调（档位仍在各自的 `outputOptions` 里，
 * `1` 必须在其中，否则默认值落在选项外、控件显示为空）。
 * 上一版默认 6 / 3 / 4：用户按下生成先拿到一叠图，而他要的只是发一条。
 *
 * ⚠️ 改默认值必须**同时**改页面 `DEFAULTS.outputs` —— 首次进入（无 `?channel=`）
 * 那条路径不经过本文件，走的是页面草稿的初始值。两处都改了，默认才真的等于 1。
 */
export const SOCIAL_CHANNELS: SocialChannel[] = [
  {
    id: 'xhs',
    name: '小红书种草图',
    description: '封面钩子 + 生活化场景，Inspiration-first，被收藏优先于被点击',
    structure: [
      '封面 · 钩子标题 + 产品主体，第一眼决定点击率',
      '场景 · 生活化使用瞬间，有真实环境与光线',
      '细节 · 局部质感特写，证明做工',
      '对比 · 使用前后 / 有它没它',
      '多角度 · 正侧背或展开状态',
      '总结 · 清单式收尾，适合截图保存',
    ],
    outputs: 1,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '3:4',
    ratioOptions: SOCIAL_RATIOS,
    textRule: '封面文字占画面 30-50%（标题 6-12 字，位置避开产品主体）；内页文字压到 10-20%',
    visualRule: '真实拍摄感优先，拒绝摄影棚硬光与过度修图；色调统一、可带轻微胶片或生活气息',
    resolutionDefault: '2k',
    estimatedMs: 60000,
    /* 图与稿同口径：小红书种草图配小红书种草文案（标题 + 正文 + 话题标签） */
    copyChannel: 'xhs',
  },
  {
    id: 'moments',
    name: '朋友圈配图',
    description: '私域生活流，画面自己说话，不摆拍不硬广',
    structure: ['主打图 · 产品干净特写', '场景 · 日常使用的一瞬间', '细节 · 材质与质感特写'],
    outputs: 1,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '1:1',
    ratioOptions: SOCIAL_RATIOS,
    textRule: '文字不超过画面 20%，能不加就不加；私域靠画面叙事，不靠文案堆砌',
    visualRule: '手机随手拍的质感与自然光，刻意留一点生活杂乱感，避免样张式的完美',
    resolutionDefault: '2k',
    estimatedMs: 45000,
    /* 私域配图 → 私域短文案。这一档的文案页签也是 `moments`，不是 `xhs` */
    copyChannel: 'moments',
  },
  {
    id: 'overseas',
    name: 'Instagram / FB 配图',
    description: '海外社媒图文流：Inspired lifestyle，被 Save 与收藏优先，不是被点击优先',
    structure: [
      '主图 · 成品感画面，主体干净、构图留白，一眼能看出「这是什么生活」',
      '场景 · 真实生活场景中的使用瞬间，有人但不需要露脸',
      '细节 · 材质与工艺的近距离特写，画面自己说明品质',
      '氛围 · 与生活方式绑定的环境空镜（桌面 / 窗外 / 户外）',
    ],
    outputs: 1,
    outputOptions: [1, 2, 3, 4],
    ratioDefault: '4:5',
    ratioOptions: SOCIAL_RATIOS,
    textRule: '图上文字压到 15% 以内，只允许英文短句（≤ 6 个词）；海外用户把「图上堆字」直接读成广告',
    visualRule: '自然光与真实质感，低饱和、可带轻微胶片颗粒；不要国内电商式的高饱和硬光与浮夸排版',
    resolutionDefault: '2k',
    estimatedMs: 50000,
    /*
     * ⚠️ 这一条的 id **与图档不同名**：图档叫 `overseas`（海外），文案档叫
     * `social-intl`。这正是「映射必须写目标档真 id」的价值 —— 靠名字推断
     * （`copyChannel: id`）在这里会直接落空，而落空的表现是**文案档找不到、
     * 静默退到第一条**（小红书笔记），出来的稿是中文种草体、还带话题标签。
     */
    copyChannel: 'social-intl',
  },
]

/* -------------------------------------------------------------------------- */
/* 数字人                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * 数字人**不是视频工作流的一个场景**，而是独立能力 ——
 * 「商品主图视频」的输入是产品图，数字人的输入是形象 + 口播稿，
 * 两者连进料结构都不一样，混在一个下拉里会让用户选了之后不知道该填什么。
 *
 * 合规红线（写死在配置里，不给 UI 绕过）：
 * 克隆真人形象必须持有本人书面授权，且克隆形象默认带 AI 生成标识要求。
 * 这条不做成「可选项」，因为漏做一次就是肖像权纠纷。
 */
export type AvatarSource = 'ai' | 'clone'

/**
 * 形象的**来源**（2026-10-05 语义修正）。
 *
 * ⚠️ 这一版把「AI 生成形象」从「按文字描述生成一张脸」改成了
 * **「引用 AI 模特 / 上传人像照片」** —— 用户原话：
 * 「形象与素材这里直接引用 ai 模特或者上传人像才对」。
 *
 * 原因是上游换了：数字人走 RunningHub InfiniteTalk 对口型，它要的是一张**真实的人脸图**
 * （LoadImage 节点），不是一段文字描述。文字描述只能生成「一个像那么回事的人」，
 * 而口型驱动需要的是有五官结构的照片 —— 拿描述去换脸，出来的片子嘴型对不上、
 * 脸还会中途变形，且**不报错**。
 *
 * 所以「形象描述」那个自由文本框随之下线（它曾是这个来源的唯一依据），
 * 换成两个真入口：**从素材库挑一张已生成的 AI 模特图**，或**本地上传人像照片**。
 * 两者在界面上是**同一个字段**（`RefUploader` 的上传区 + 「从素材库选择」），
 * 不拆成第二次二选一 —— 同一张脸的两种取法，不该问两遍。
 *
 * `needConsent` 仍然是「这条来源要不要书面授权」的唯一判据：
 * 引用自己生成的 AI 模特不需要；克隆真人（`clone-avatar` 档）一定需要。
 */
export const AVATAR_SOURCES: { id: AvatarSource; name: string; description: string; needConsent: boolean }[] = [
  {
    id: 'ai',
    name: 'AI 模特 · 人像照片',
    description: '引用素材库里的 AI 模特图，或上传一张正脸人像。无肖像权风险，可直接商用',
    needConsent: false,
  },
  {
    id: 'clone',
    name: '克隆真人形象',
    description: '上传真人形象素材克隆。必须持有本人书面授权，授权文件与克隆形象一并归档',
    needConsent: true,
  },
]

/**
 * 音色 —— **原文源 `VOICE_OPTIONS` 于 2026-10-06 删除**。
 *
 * 用户口径：「数字人的音色，使用一下 apilio-tts 里面的接口」。
 *
 * 旧版这里硬编码了 5 条（`id` 给界面用、`tts` 是发给上游的音色 ID）。它有两个毛病：
 *   ① 「上游认哪些音色」的真源在 skill 那边（88 条实测），本地这 5 条是它的一个子集，
 *      而两处清单必然漂移 —— skill 删一条，这里照发，症状是用户选了 A、出来是 B；
 *   ② 多一层 `id → tts` 映射，读代码的人得追两步才知道发出去的是什么。
 *
 * 现在：清单走 `GET /api/aigc/voices`（后端从 skill 的 `SYSTEM_VOICES` 现读），
 * **`form.voice` 直接存上游音色 ID**；兜底清单与旧草稿迁移表在 `config/voices.ts`。
 */

/**
 * 数字人页固定使用的模型 slug。
 *
 * ⚠️ **两处互指**：后端 `app/registry.py` 的那条 `_vid("runninghub-infinitetalk", …)`
 * 是同一个字符串，它**故意不进 `FAMILIES`**（数字人的进料是「人脸图 + 音频」，
 * 跟视频页那些「提示词 + 参考图」的档混在一个下拉里必然被误选）。
 * 所以这一页不走 `ModelSelect`，直接把这个 slug 提交上去 ——
 * `resolve()` 认 `ROUTE_BY_SLUG`，认得出它。
 *
 * 改这里必须同时改后端那条路由；反之亦然。两处都写着这条注释就是为了那一刻。
 */
export const DIGITAL_HUMAN_MODEL_SLUG = 'runninghub-infinitetalk'

/**
 * 「数字人分镜图」这一档的 app_slug（2026-10-06 新增）。
 *
 * 它是数字人页**第 1 步的产物**：模特图（+ 可选产品图）按一段「场景 / 动作」描述
 * 生成一张出镜画面，这张图再作为数字人的形象图喂给对口型接口。
 *
 * 为什么**独立一个 app_slug**，而不是复用现有档：
 *   · 「AI 模特图」（`product-product-model`）是 `textOnly` —— 它刻意不收参考图，
 *     而这一步要把模特图与产品图一起当参考（图生图），进料结构不同；
 *   · 产物要能**按来源追溯**：`digital-board` 的产出就是「这张分镜图」，
 *     右栏与资产库靠 slug 分辨它，混进 product 档会分不清是模特图还是分镜图。
 *
 * ⚠️ **三处互指**：后端 `registry.APP_KIND` 显式钉它是 `image`、
 * `taxonomy._BY_SLUG` 把它归到 `video`（它是这支片子的第 0 帧）。
 * slug 里没有 `image`/`video` 字样，两处都靠推断会掉进兜底分支。
 */
export const DIGITAL_BOARD_APP_SLUG = 'digital-board'

/**
 * 分镜图走哪个生图模型族 —— 与全站默认生图族一致（`modelFamilies.DEFAULT_FAMILY_ID.image`）。
 *
 * 这里刻意**不做模型下拉**：数字人页的用户在做视频，不关心出图的模型选型；
 * 摆一个下拉只会把「视频档」的语境带进来（同 `ENGINE_NAME` 那一行的处理）。
 * 哪天要放开，先在这一页长出控件，再把值写进载荷。
 */
export const DIGITAL_BOARD_FAMILY = 'gpt-image-2.5'

/**
 * 分镜图**默认**出几张 —— 4 张让用户有得挑（挑中的那张才成为形象图）。
 *
 * ⚠️ 2026-10-07 起它只是**默认值**，不再是唯一取值：页面里长了「出几张」的
 * 选择器（1 / 2 / 4），用户改过的值存在草稿的 `boardOutputs` 里。
 * 留着这个常量做默认值的真源（不要把 4 再写进页面），否则「默认几张」就有两份。
 * 起因是用户看到「（4 张，独立计费）」直接问「**怎么4张的？**」——
 * 多出几张是为了有得挑，但那是取舍，该由掏钱的人定。
 */
export const DIGITAL_BOARD_OUTPUTS = 4

export type DigitalHumanMode = {
  id: 'talking-head' | 'clone-avatar'
  name: string
  description: string
  /** 按这个节奏写口播稿，时间轴与段落一一对应 */
  structure: string[]
  duration: number
  durationOptions: number[]
  /**
   * 自定义时长的允许范围（秒）—— 「时长」控件旁那个数字框的夹取依据（2026-10-09）。
   *
   * ⚠️ 这一档**没有上游硬上限**：成片长度由配音实际长度决定，时长只用来给稿子
   * 定长（见页面「时长」那格的 hint）。所以范围是**产品口径**而非能力上限 ——
   * 下界 5 秒（再短连一句卖点都念不完），上界 180 秒（够一条 3 分钟的稿，
   * 再长就该分几条片子拍，而不是让用户等一条十几分钟的成片）。
   */
  durationMin: number
  durationMax: number
  ratioDefault: string
  ratioOptions: { value: string; label: string }[]
  /**
   * ⚠️ 曾经有个 `needScript: boolean`，2026-10-05 删掉了。
   *
   * 它区分「口播要稿 / 建档不要稿」—— 而接上 InfiniteTalk 之后**两档都必须有稿**
   * （没有稿就没有音频，对口型无从谈起）。一个恒为 true 的开关不是配置，
   * 是负债：下一个人会以为存在 false 那一路，去找一条不存在的分支。
   *
   * ⚠️ 2026-10-06 又删了一个 `estimatedMs: number`（= 480000，8 分钟），同一条理由。
   *
   * 出片耗时**随配音长度线性涨**（实测：11.47s 音频 → 91s，约 7.9 倍音频时长），
   * 而这一档的时长选项是 15/30/60 秒 —— 差 4 倍。写死一个常量必然有一头是假话：
   * 按 8 分钟算，15 秒片要虚报 5 分钟（用户以为卡住了），60 秒片又要少报。
   * 现在改成从 `form.duration` 现算（见 `DigitalHumanStudio` 的 `estimateMs`），
   * 一档一个常量变成了一个公式。
   */
}

export const DIGITAL_HUMAN_MODES: DigitalHumanMode[] = [
  {
    id: 'talking-head',
    name: '数字人口播',
    description: '一张形象图（AI 模特或人像照）+ 一段口播稿 → 带口型的口播视频',
    structure: [
      '0-3s 开场留人：抛出问题或结果，不做自我介绍',
      '3-8s 产品出场：展示外观与使用场景',
      '8-15s 核心卖点：一条一个信息点，配对应画面',
      '15s+ 异议处理与逼单：讲清顾虑与优惠，给明确行动指令',
    ],
    duration: 15,
    durationOptions: [15, 30, 60],
    durationMin: 5,
    durationMax: 180,
    // ⚠️ 成片比例**跟随形象图**（上游不接受指定）。这个值只用于结果卡预览框的宽高比，
    // 不是"用户选的比例"—— 界面上已经没有比例控件了（2026-10-05 五改）。
    ratioDefault: '9:16',
    ratioOptions: [
      { value: '9:16', label: '9:16 竖版（抖音/视频号）' },
      { value: '16:9', label: '16:9 横版（官网/详情页）' },
      { value: '1:1', label: '1:1 方图（信息流）' },
    ],
    // 出片耗时不在这里写死 —— 它随配音长度线性涨，见上面 `DigitalHumanMode` 的注释。
  },
  {
    id: 'clone-avatar',
    // 2026-10-05 由「形象克隆」改名。原来的语义是「建档，供后续口播复用」——
    // 而 InfiniteTalk **没有建档能力**，它是「给图给音出一次片」。
    // 留着旧名字等于承诺一个上游不提供的服务（用户按下「建档」会拿到一支视频）。
    // 现在它诚实地表示：用**已授权的真人素材**出镜口播（老板/主理人亲自讲）。
    name: '真人出镜',
    description: '用已授权的真人素材出镜口播，与 AI 模特档共用同一条上游',
    structure: [
      '0-3s 开场留人：用本人的身份建立信任，不要自我介绍流水账',
      '3-8s 产品出场：手持或展示实物',
      '8-15s 核心卖点与使用场景',
      '15s+ 顾虑与逼单：给出明确行动指令',
    ],
    duration: 15,
    durationOptions: [15, 30, 60],
    durationMin: 5,
    durationMax: 180,
    ratioDefault: '9:16',
    ratioOptions: [
      { value: '9:16', label: '9:16 竖版（抖音/视频号）' },
      { value: '16:9', label: '16:9 横版（官网/详情页）' },
    ],
  },
]

/**
 * 口播用途。
 *
 * 「讲解」和「带货」用的是同一个模型、同一套参数，差别在**稿子的意图**：
 * 讲解型靠信息密度留人（是什么 / 怎么用 / 适合谁），带货型靠节奏留人
 * （痛点 → 演示 → 价格锚点 → 逼单）。
 * 不区分的话，两种需求都会拿到同一份「既像科普又像叫卖」的四不像稿子。
 */
export type DigitalHumanUse = 'explain' | 'sell'

export const DIGITAL_HUMAN_USES: { id: DigitalHumanUse; name: string; description: string }[] = [
  { id: 'explain', name: '数字人讲解', description: '知识型口播：是什么 / 怎么用 / 适合谁，靠信息密度留人' },
  { id: 'sell', name: '数字人带货', description: '转化型口播：痛点 → 演示 → 价格锚点 → 逼单，靠节奏留人' },
]

/* -------------------------------------------------------------------------- */
/* 模板中心                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * 模板中心的分类 —— 2026-09-29 全部换过一批。
 *
 * 上一版是按**我们的能力**分类（电商主图 / 商品场景图 / 展台背景 / 营销素材 /
 * 详情页 / AI 视频），共 6 类 12 套，封面是扁平矢量插画。
 * 用户看了一圈，原话「现在的我觉得不好，现在删除了先」，并给了四张参考截图标明要什么：
 * 短视频爆款 / 套图爆款 / 社媒模板 / 文案模板。
 *
 * 差别不只是名字：**旧版按能力分，新版按「用户想抄哪个东西」分**。
 * 用户进模板中心的动机是「我看到别人家一条视频火了，我也想做一个同款」，
 * 他脑子里没有「营销素材」这个词，只有「那条会动的服装」。所以分类改成内容形态。
 *
 * 封面同步换成**真实摄影**（`scripts/template-assets.json` 用 duomi-image-2 生成）。
 * 插画封面在模板墙上是负资产：模板墙卖的是「出来大概长什么样」，
 * 插画给不出这个信息，用户点进去才发现不是想要的东西。
 */
export type TemplateCategoryId = 'VIDEO_HOT' | 'SET_HOT' | 'SOCIAL_HOT' | 'COPY_HOT' | 'CASE_HOT' | 'MY_TPL'

export type TemplateItem = {
  id: string
  name: string
  description: string
  category: TemplateCategoryId
  /** 落地地址（带参）。**必须落在真实存在的路由与参数上**，改名等于把入口做空 */
  to: string
  /**
   * 封面（public/templates/<id>.webp），**真实摄影，不是插画**。
   * 生图清单 scripts/template-assets.json，批量生成 scripts/gen-template-assets.py。
   * 不要再退回随机图床占位图或矢量插画 —— 封面必须与模板语义对应。
   */
  cover: string
  /**
   * 参考站原图上的传播量角标（如 `92w`）。**只作展示，不是我们的数据**。
   * 功能性模板（套图 / 文案）没有这一项，卡片上就不渲染角标。
   */
  views?: string
  /**
   * 「原图」—— 只对**案例**类（`CASE_HOT`）有意义（2026-10-11 新增）。
   *
   * 填了它，案例卡封面就渲染成**并排对照**：左「原图」→ 右「成品」
   * （见 `TemplateGallery.tsx` 的 `CaseCompare`，网格卡与案例墙共用一份），
   * 让 4-5 线客户一眼看懂「随手拍的原图 → 我们能做成的成品」。这里放那张**真实原图**
   * 的地址（与成品封面同一批真实摄影资产，目录同为 `public/templates/`）。
   *
   * ⚠️ **没有原图时就不要填**：卡面退回单张成品封面，而不是编一张假原图 ——
   * 借一张不相干的图冒充当「原图」正是本类明令禁止的（本项目对「封面语义必须对得上」
   * 的硬约束，见本文件头部旧版插画封面的教训）。
   */
  before?: string
  /**
   * 行业 / 品类（2026-10-11 新增）—— 「**我是做什么的**」这一维。
   *
   * ## 为什么必须有它
   *
   * 模板原本只按**内容形态**分（短视频 / 套图 / 社媒 / 文案），那是「拍成什么样」
   * 的视角。可 4-5 线制造业客户找模板时，脑子里蹦出来的第一句话是
   * 「我是做五金的」，不是「我要一条短视频」—— 他们**先按行业认领，再看形态**。
   * 旧四类里没有这一维，症状就是「翻完二十几张，没有我的那张」。
   *
   * ## 写法
   *
   * 填**客户自己的叫法**：家居日用 / 服饰配饰 / 食品保健 / 卫浴洁具 / 数码配件 /
   * 厨房小电 / 收藏摆件 / 户外装备 …… 口径取 `product_assets` 里客户自建的那 9 个
   * 分类（`GET /api/aigc/product-categories`），**不要写我们的内部分类名**，
   * 也不要写行业黑话。全能型模板（白底主图、去背景）填「全品类」——
   * 它们是任何行业都要做的第一步，硬塞一个行业反而把其它行业挡在门外。
   *
   * ⚠️ **先只在图片类（`SET_HOT`）上填**：那一类是客户主线（图为主、视频降级），
   * 也正是这一维最缺的地方。其余分类暂缺该字段时卡面**不渲染**这枚标签
   * （不是渲染成空白），等放量时按同一口径补齐 —— 别只补一半。
   */
  industry?: string
  /**
   * 这条是**用户自己存下来的**（来自 `video_templates` 表，见 `customToTemplateItem`），
   * 不是 `TEMPLATES` 里的代码资产。
   *
   * ⚠️ 它只有一个消费方：模板中心据此渲染「改名 / 删除」两颗键。
   * `TEMPLATES` 里的内置条目**没有**这个字段 —— 内置的改不了，
   * 这也正好是「能按下删除键」的判据（内置条目根本不该有那颗键）。
   */
  custom?: boolean
}

export const TEMPLATE_CATEGORIES: { id: TemplateCategoryId | 'ALL' | 'FAVORITES'; name: string; desc: string }[] = [
  { id: 'ALL', name: '全部模板', desc: '预设与你自己存的模板一次看全' },
  /**
   * 「我的模板」（2026-10-08 新增）。
   *
   * 用户原话：「现在模板中心可以增加自定义模板，或者是用户改完模板可以存储起来」。
   * 它与下面四类**不是同一批东西**（那四类是随版本发布的代码资产），
   * 所以排在四类之前、紧跟「全部模板」—— 用户自己刚存的那条应该最先被看见，
   * 而不是滚过二十多张预设卡才找到。
   *
   * ⚠️ 它**不进 `TEMPLATES`**：清单来自 `video_templates` 表（按账号、异步），
   * 由 `templatesOfCategory()` 的第三个参数传进来 —— 与本项目「模板真源只有一份」
   * 的原则一致：`TEMPLATES` 管内置，自存模板的真源是后端那张表。
   * 因此 `export-template-count.ts` 报出的总数不含它们（那个数只数代码资产）。
   */
  { id: 'MY_TPL', name: '我的模板', desc: '你在工作台存下来的那一套：参数 + 分镜 + 全片约束，换个产品套用会重新拼提示词' },
  { id: 'FAVORITES', name: '我的收藏', desc: '存在本机，换设备不同步' },
  /**
   * 「案例」（2026-10-11 新增）。
   *
   * ## 为什么要它
   *
   * 面向的客户是 **4-5 线制造业 / 农业等经典企业**（企业自用、用户规模不小），
   * 这类人**没有美工、不会写提示词**。他们进模板中心的动机不是「我想调参数」，
   * 而是「别人家这个产品做出来长什么样，我要一个同款」。所以这一类的卡面
   * **按「品类」组织**（3C / 家居 / 服饰 / 食品 / 五金 / 礼盒），
   * 每张都是可以直接用的成品样例，按钮写「直接做」——看中就一键带着同款参数进工作台。
   *
   * ## 它与「套图爆款 / 短视频爆款」的区别
   *
   * 那四类是**按内容形态**分（拍成什么样子），这一类**按客户自己的产品品类**分，
   * 站的是使用者的视角（「我是做五金的」），不是我们的能力视角。放在四类之前，
   * 让不熟悉的用户第一眼先看到「我这个行业能做成什么样」。
   *
   * ## ⚠️ 封面不许造
   *
   * 本类的封面**复用现有的真实摄影封面**（`/templates/*.webp`），按品类**语义对应**
   * 挑（3C 案例用 3C 的图、家居案例用家居的图）。绝不新画插画、也不新造拍摄 ——
   * 封面与语义对不上时，用户点进去才发现不是想要的东西（见本文件头部的旧版教训）。
   */
  { id: 'CASE_HOT', name: '案例', desc: '按品类看成品样例 —— 看中哪张，点「直接做」带着同款参数进对应工作台' },
  { id: 'VIDEO_HOT', name: '短视频爆款', desc: '抖音 / 视频号在跑的爆款结构，点进去直接铺好整条视频参数' },
  { id: 'SET_HOT', name: '套图爆款', desc: '一套图讲清一个产品，从展示图到场景图到模特穿搭' },
  { id: 'SOCIAL_HOT', name: '社媒模板', desc: '小红书 / 朋友圈 / Instagram 图文封面与配图' },
  { id: 'COPY_HOT', name: '文案模板', desc: '标题 / 带货口播 / 探店 / 海报文案，一次出齐' },
]

export const TEMPLATES: TemplateItem[] = [
  // —— 短视频爆款 ——
  // 这 8 条与 `config/videoTemplates.ts` 的短视频模板**是同一批**（同封面、同名字），
  // 用 `?vtpl=<id>` 深链过去，工作台按 id 找模板铺参数。
  // 唯一真源在 videoTemplates.ts：这里只写「模板中心怎么展示它」，
  // 参数与封面路径不在这里抄第二份。
  // 2026-09-30：前 6 条整体换过一批 —— 原来的「玩法模板」（会动的服装 / 武术舞蹈 /
  // 3D 地图 / 卡点舞 / 红绿灯变装 / 实景动画）用户判为「不实在」，换成他自己跑过的
  // 带货模板（4 LOOK 闪切 / 地铁换装 / 单衣卡点 / 镜子卡点 / 5 角度看版型 / 通勤风穿搭）。
  // 2026-10-05：先补 3 条普通商品（3C 数码 / 日用百货 / 家居好物），用户随后判
  // 「衣服的类型少点」，服装从 6 条砍到 3 条 —— 删掉 vt-02 地铁换装 / vt-04 镜子卡点 /
  // vt-05 5 角度看版型（路径与留下的三条重复，判定见 videoTemplates.ts 头部硬约束 6）。
  // 同日稍后：用户判「带货短视频 / 商品视频 感觉差不多，需要合并」，两档并成一档 ——
  // 商品视频档那四条里三条与这里重复被删，只留 `mv-02 360° 旋转展示` 并进本分类
  // （它此前**从没进过模板中心**，只在工作台切到商品视频档时才看得到）。
  // 现在 8 条 = 服装 3 + 通用结构 1 + 普通商品 3 + 转台展示 1。
  //
  // ⚠️ 条数本身不在这里写死期望值：`scripts/export-template-count.ts` 现算，
  // `verify-templates.py` 拿它与工作台的 `templatesForMode('short-video')` 对数。
  { id: 'vt-01', name: '4 LOOK 极速闪切', description: '四套造型按重拍硬切轮播，闭场回闪翻倍信息密度，完播率靠节奏拿', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-01', cover: '/templates/vt-01.webp' },
  { id: 'vt-03', name: '单品卡点 · 单衣细节闪切', description: '平铺 → 细节 → 上身 → 定格，一件单品的质感与版型一次讲透', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-03', cover: '/templates/vt-03.webp' },
  { id: 'vt-06', name: 'UGC 种草 · 通勤风穿搭', description: '通勤场景跟拍，无口播、全片无字，靠画面与氛围带出穿搭欲望', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-06', cover: '/templates/vt-06.webp' },
  // vt-07 与其他条不是一类：其他条讲「拍成什么样子」，它是**结构**
  // （一条片子怎么把东西卖出去）。换批之后短视频档**一律不标 `views`** ——
  // 角标标的是「参考站上的传播量」，我们自己的模板没有这个数，挂着就是假指标。
  { id: 'vt-07', name: '带货脚本 · 15秒成交结构', description: '6 镜头标准成交结构：钩子 → 产品 → 使用 → 细节 → 场景 → 收尾', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-07', cover: '/templates/vt-07.webp' },
  // ⚠️ 与本文件其他分类一样，**这里不许出现只有这里才有的名字** —— 卡面文案的真源是
  // `videoTemplates.ts` 的 `templateTitle()`，两边对不上时模板中心与工作台会显示两个名字。
  { id: 'vt-08', name: '品牌大片 · 3C 数码硬核开场', description: '连续推近把产品当主角拍，一条片子讲清工业设计，适合客单价高、要建立信任的数码硬货', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-08', cover: '/templates/vt-08.webp' },
  { id: 'vt-09', name: '口播带货', description: '真人出镜把卖点一条条讲透，靠讲解密度成交，适合克单价低、决策快的日用百货', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-09', cover: '/templates/vt-09.webp' },
  { id: 'vt-10', name: '使用演示 · 家居好物实测', description: '在真实居家环境里演一遍使用全过程，让效果先说话再谈卖点，家居品类最吃这一套', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-10', cover: '/templates/vt-10.webp' },
  // mv-02 是并档时从原「商品视频」档留下来的唯一一条（另三条与 vt-08 / vt-10 是同一类
  // 拍法，已删）。它的 id 前缀仍是 `mv-` —— **刻意不改**：id 同时是封面文件名与
  // `?vtpl=` 深链的键，改名要连封面与生图缓存一起动，而用户看不到 id。
  // 深链的 mode 写 `short-video`（合并后唯一的主力档）—— 写 `main-video` 虽然靠回落
  // 也能进，但那是给**旧地址**用的兼容路径，新卡片不该依赖它。
  { id: 'mv-02', name: '转台展示 · 360° 旋转', description: '匀速转台把产品转满一圈，结构、做工与各面细节一次交代完，主图位的通用解法', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=mv-02', cover: '/templates/mv-02.webp' },
  // 2026-10-09 补库五条：填满短视频档 12 个场景里原先零承载的五个（UGC 软广 / 促销 /
  // 场景展示 / 细节推近 / 产品展示）。文案与 videoTemplates.ts 的 tag + name + desc 同源，
  // 上下两处对不上时模板中心与工作台会显示两个名字。
  { id: 'vt-11', name: '素人验效 · 前后对比实测', description: '同一机位拍「用前 / 用后」，靠效果对比成交，美妆个护与洗护清洁最吃这一套', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-11', cover: '/templates/vt-11.webp' },
  { id: 'vt-12', name: '大促直给 · 一份钱给多少', description: '一整套一次摆开、逐件亮相，靠「给得多」的画面量感成交，食品饮料与囤货型日用适用', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-12', cover: '/templates/vt-12.webp' },
  { id: 'vt-13', name: '场景代入 · 母婴宠物日常', description: '在真实居家日常里演一段照顾关系，靠人与物的互动带出代入感，母婴与宠物品类适用', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-13', cover: '/templates/vt-13.webp' },
  { id: 'vt-14', name: '工艺微距 · 材质与做工', description: '以质感特写为主体，让材料表面与工艺接缝自己说话，适合茶器、饰品与皮具五金', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-14', cover: '/templates/vt-14.webp' },
  { id: 'vt-15', name: '套系陈列 · 一整套摆开', description: '俯拍把整套一次摆开、逐件揭开，卖的是「齐不齐、怎么配」，礼盒与多件套适用', category: 'VIDEO_HOT', to: '/video-studio?mode=short-video&vtpl=vt-15', cover: '/templates/vt-15.webp' },

  // —— 套图爆款 ——
  // 「商品套图」形态 2026-09-30 下架（用户原话「这套图跟详情页重复了」），
  // st-02 原来落 `?mode=set`，改落「电商主图」——它的产出物本来就是主图那一套
  // （白底 + 场景 + 卖点 + 细节），落到别的形态反而对不上它的封面语义。
  { id: 'st-01', name: '3C数码产品展示图', description: '净底 + 花艺道具点缀，3C 标品的清爽展示图', category: 'SET_HOT', industry: '数码配件', to: '/detail-page?mode=main', cover: '/templates/st-01.webp' },
  { id: 'st-02', name: '家居家电场景图生成', description: '真实居家空间与自然光，让买家先代入再买', category: 'SET_HOT', industry: '家居日用', to: '/detail-page?mode=main', cover: '/templates/st-02.webp' },
  // st-03「虚拟模特穿搭图生成」2026-10-10 删 —— 它的深链是
  // `/professional/fashion?feature=model-gen`，而那一档就是同日删掉的「模特生成」
  // （用户："这个就不要了，重复了"，见 FASHION_FEATURES 上方的说明）。档没了这张卡
  // 会**静默**落到别的档（`findFeature` 回落 `list[0]`），所以连卡一起下架。
  // 封面 `/templates/st-03.webp` 留着（资产文件不追删），只是不再有卡引用它。
  { id: 'st-04', name: '美妆产品上妆效果图', description: '同一张脸的前后对比，上妆效果一图讲清', category: 'SET_HOT', industry: '美妆个护', to: '/professional/products?feature=product-model', cover: '/templates/st-04.webp' },
  /* --------------------------------------------------------------------------
   * 2026-10-11 扩库：图片类 3 → 13 条。
   *
   * ## 起因
   *
   * 用户原话「模板中心的模板不够多」。查完发现真正的缺口**不是总条数**，
   * 而是**图片类只有 3 条** —— 而客户是「图为主线、视频降级」，图恰恰是他的
   * 主线；更扎心的是那 3 条里**没有一条是制造业视角**（3C数码 / 家居家电 /
   * 美妆上妆），客户打开图片分类看到的是三张跟他无关的图。
   *
   * ## 补法
   *
   * 不是「再堆几张好看的图」—— 模板墙卖的是「出来大概长什么样」，
   * **图不对比图少更糟**（见本文件顶部旧版插画封面的教训）。所以是
   * **把工作台早就跑得通、模板墙上却没有的产出补上**：
   *
   *   · 图片类档位实际有 20+ 个（`PRODUCT_FEATURES` 8 + `FASHION_FEATURES` 9 +
   *     `IMAGE_TOOLS` 6 + 社媒 3），模板墙上原先只落了 3 张；
   *   · 下面 10 条**逐条对应真实存在的档位** —— `to` 必须是活的深链，
   *     写错 slug 会被 `*` 兜底弹回首页、点下去没反应（本文件老规矩）。
   *
   * ## 三条口径
   *
   *   ① **先补客户最长走的那几步**（白底主图 → 多角度 → 场景 → 细节 → 包装），
   *      按客户的使用顺序排，不是按我们觉得「高级」的排；
   *   ② **「手机随手拍 → 能上架」这条链必须有卡**（`st-13 去背景`）——
   *      客户最常见的一幕就是拿手机拍一张、然后要「弄成能上架的」，
   *      而这一类此前**一张卡都没有**；
   *   ③ 每条都带 `industry`（见 `TemplateItem.industry`）：4-5 线客户是
   *      「我是做五金的」这样找模板的，而这一维以前**整个缺失**，这是最小落地。
   *
   * ## 封面
   *
   * 同样走既定管线（`scripts/template-assets.json` + `scripts/gen-template-assets.py`，
   * duomi-image-2 真实摄影），**不用库里那 215 张运行期成品图** ——
   * 它们在 `backend/data/uploads/`，而 `backend/.gitignore` 明写
   * 「运行时数据不进版本控制」。那些图只当**选型参考**（告诉我们哪些产出
   * 值得做成卡），不当资产。
   * ------------------------------------------------------------------------ */
  { id: 'st-05', name: '白底主图 · 标品净底', description: '纯白影棚 + 柔和落地影，标品上架的第一步；不挑品类，谁都用得上', category: 'SET_HOT', industry: '全品类', to: '/professional/products?feature=product-main', cover: '/templates/st-05.webp' },
  { id: 'st-06', name: '多角度展示 · 一件转着看', description: '同一件货四个角度一次出齐，省掉反复重拍与角度凑不齐', category: 'SET_HOT', industry: '数码配件', to: '/professional/products?feature=product-multi-angle', cover: '/templates/st-06.webp' },
  { id: 'st-07', name: '场景图 · 居家实景', description: '放进真实居家光里，让买家先代入「摆在我家是什么样」', category: 'SET_HOT', industry: '家居日用', to: '/professional/products?feature=product-scene', cover: '/templates/st-07.webp' },
  { id: 'st-08', name: '细节特写 · 材质与做工', description: '微距交代材质与接缝，卖点是「看得出做得扎实」', category: 'SET_HOT', industry: '五金陶瓷', to: '/professional/products?feature=product-detail', cover: '/templates/st-08.webp' },
  { id: 'st-09', name: '包装礼盒 · 开盒展示', description: '半开盒 + 内衬特写，卖的是「送礼拿得出手」', category: 'SET_HOT', industry: '食品保健', to: '/professional/products?feature=product-packaging', cover: '/templates/st-09.webp' },
  { id: 'st-10', name: '展台背景图 · 有布景的净底', description: '净底之上加一层布景，像品牌目录页那样摆出来', category: 'SET_HOT', industry: '收藏摆件', to: '/professional/products?feature=product-booth', cover: '/templates/st-10.webp' },
  { id: 'st-11', name: '服装挂拍 · 无模特', description: '衣架挂起、平整无皱，不请模特也能上架', category: 'SET_HOT', industry: '服饰配饰', to: '/professional/fashion?feature=garment-hang', cover: '/templates/st-11.webp' },
  { id: 'st-12', name: '白底平铺 · 正背面', description: '俯拍平铺、正背面都给，面料纹理与版型看得清', category: 'SET_HOT', industry: '服饰箱包', to: '/professional/fashion?feature=garment-flat-lay', cover: '/templates/st-12.webp' },
  { id: 'st-13', name: '去背景 · 随手拍变净底', description: '手机随手拍也能出干净净底 —— 上架前最要紧的一步', category: 'SET_HOT', industry: '全品类', to: '/img-localize?tool=remove-bg', cover: '/templates/st-13.webp' },
  { id: 'st-14', name: '小红书种草图 · 竖版封面', description: '生活场景 + 竖版构图，专为小红书封面位而做', category: 'SET_HOT', industry: '全品类', to: '/social-image?channel=xhs', cover: '/templates/st-14.webp' },

  // —— 社媒模板 ——
  // 这五条落 `social-image`（小红书 / 朋友圈 / IG）而不是视频工作台：
  // 短视频那 6 条已经占了视频侧，社媒模板若也跳视频页，两个分类功能完全重合，
  // 用户会在两个入口之间来回试。这里按**图文封面**归位。
  { id: 'sm-01', name: '学功夫练武术舞蹈挑战', description: '国风动作挑战，小红书竖版封面与内页', category: 'SOCIAL_HOT', to: '/social-image?channel=xhs', cover: '/templates/sm-01.webp' },
  { id: 'sm-02', name: '3D地图转场', description: '3D 地图视角，适合目的地 / 跨境选题的 IG 图文', category: 'SOCIAL_HOT', to: '/social-image?channel=overseas', cover: '/templates/sm-02.webp' },
  { id: 'sm-03', name: '撞车营销广告', description: '生活场景里的产品入镜，海外信息流图文', category: 'SOCIAL_HOT', to: '/social-image?channel=overseas', cover: '/templates/sm-03.webp' },
  { id: 'sm-04', name: '方向盘变装工作流', description: '车内氛围光影 + 变装，小红书竖版图文', category: 'SOCIAL_HOT', to: '/social-image?channel=xhs', cover: '/templates/sm-04.webp' },
  { id: 'sm-05', name: '牛来卡点舞', description: '纯色背景人像封面，私域朋友圈配图', category: 'SOCIAL_HOT', to: '/social-image?channel=moments', cover: '/templates/sm-05.webp' },

  // —— 文案模板 ——
  { id: 'ct-01', name: '抖音爆款文案生成', description: '3 秒钩子 + 一口一段的带货口播稿', category: 'COPY_HOT', to: '/copywriting?channel=douyin&group=video', cover: '/templates/ct-01.webp' },
  { id: 'ct-02', name: 'Ai带货文案撰写模板', description: '按详情页模块顺序出稿，标题到信任背书一次齐', category: 'COPY_HOT', to: '/copywriting?channel=detail-copy&group=product', cover: '/templates/ct-02.webp' },
  { id: 'ct-03', name: 'Ai商品标题核心卖点生成', description: '高点击标题 + 词序与违规词诊断', category: 'COPY_HOT', to: '/copywriting?channel=title&group=product', cover: '/templates/ct-03.webp' },
  { id: 'ct-04', name: '探店文案生成', description: '小红书探店笔记：标题钩子 + 正文 + 话题标签', category: 'COPY_HOT', to: '/copywriting?channel=xhs&group=social', cover: '/templates/ct-04.webp' },
  { id: 'ct-05', name: '商业海报文案生成', description: '标题 / 正文 / 促销 / CTA 四段一次出齐', category: 'COPY_HOT', to: '/copywriting?channel=ad-copy&group=ad', cover: '/templates/ct-05.webp' },

  // —— 案例 ——
  // 按**客户自己的产品品类**组织（不是按我们的能力），每张是可直接用的成品样例，
  // 卡面按钮写「直接做」（见 `TemplateGallery.tsx` 的 `caseBtn` 判定）。
  //
  // ⚠️ 封面**复用现有真实摄影**、且**语义对应**：3C 案例用 `st-01` 的 3C 图、
  // 家居用 `st-02` 的居室图、服饰/食品/五金/礼盒各用对应品类的视频封面。
  // 不许为了"凑一个农产品案例"去借一张不相干的图 —— 那正是旧版插画封面翻车的原因。
  // 首次上线先覆盖制造业最常见的六个品类；2026-10-11 起接入**客户真实产品照**：
  // 农业（蜂产品，含「随手拍原图 → 成品」真对照）×2、卫浴洁具（净底 + 场景各一条）、
  // 营养保健 —— 封面全部来自客户自己发来的实拍，品类与封面语义一一对应。
  //
  // `to` 一律落在**真实存在的生成页**（与上面几类同一批路由）：图片案例落电商主图页、
  // 视频案例落短视频工作台并带 `?vtpl=`；写错 slug 会被 `*` 兜底弹回首页、点下去没反应。
  { id: 'case-01', name: '3C 数码 · 净底展示图', description: '示例成品：净底 + 道具点缀，标品清爽上架，主图位通用解法', category: 'CASE_HOT', to: '/detail-page?mode=main', cover: '/templates/st-01.webp' },
  { id: 'case-02', name: '家居家电 · 场景图', description: '示例成品：真实居家空间与自然光，让买家先代入再买', category: 'CASE_HOT', to: '/detail-page?mode=main', cover: '/templates/st-02.webp' },
  { id: 'case-03', name: '服饰 · 穿搭种草片', description: '示例成品：通勤场景跟拍，无口播、全片无字，靠画面带出穿搭欲望', category: 'CASE_HOT', to: '/video-studio?mode=short-video&vtpl=vt-06', cover: '/templates/vt-06.webp' },
  { id: 'case-04', name: '食品囤货 · 大促摆开', description: '示例成品：一整套一次摆开、逐件亮相，靠「给得多」的画面量感成交', category: 'CASE_HOT', to: '/video-studio?mode=short-video&vtpl=vt-12', cover: '/templates/vt-12.webp' },
  { id: 'case-05', name: '皮具五金 · 工艺微距', description: '示例成品：材质表面与工艺接缝特写，五金与饰品最有说服力', category: 'CASE_HOT', to: '/video-studio?mode=short-video&vtpl=vt-14', cover: '/templates/vt-14.webp' },
  { id: 'case-06', name: '礼盒套系 · 一整套摆开', description: '示例成品：俯拍整套陈列、逐件揭开，卖的是「齐不齐、怎么配」', category: 'CASE_HOT', to: '/video-studio?mode=short-video&vtpl=vt-15', cover: '/templates/vt-15.webp' },
  // 农业 · 蜂产品（2026-10-11 拿到客户真实实拍后**点亮**）。
  // 封面 = 客户发来的三瓶蜂蜜成品图；`before` = 同一批客户发来的**手机随手拍**蜂巢蜜。
  // 这一条现在是一组**真对照**：卡面上左「原图」（随手拍）→ 右「成品」并排，一眼看前后。
  //
  // ⚠️ 封面一律用**客户自己的产品照**；缺素材时宁可留空渲染「征集中」占位，
  // 也不许借一张不相干的图 —— 那正是旧版插画封面翻车的原因（见本类顶部说明）。
  { id: 'case-07', name: '农业 · 蜂产品', description: '示例成品：左「原图」是客户手机随手拍、右「成品」是做出来的样子，一眼看前后差别', category: 'CASE_HOT', to: '/detail-page?mode=main', cover: '/templates/case-07.webp', before: '/templates/case-07-before.webp' },
  // 卫浴洁具（2026-10-11，客户真实产品照）
  { id: 'case-08', name: '卫浴洁具 · 净底展示图', description: '示例成品：陶瓷洁具去背景、补光，做成清爽的净底主图', category: 'CASE_HOT', to: '/detail-page?mode=main', cover: '/templates/case-08.webp' },
  // 营养保健（2026-10-11，客户真实产品照）。
  // ⚠️ 这是**强监管品类**：保健食品广告须显著标明「本品不能代替药物」，且不得涉及
  // 疾病预防与治疗。案例只展示「净底图长什么样」，不承诺任何功效表述。
  { id: 'case-09', name: '营养保健 · 软胶囊净底图', description: '示例成品：瓶装软胶囊净底图，标签文字清晰、瓶身有体积感', category: 'CASE_HOT', to: '/detail-page?mode=main', cover: '/templates/case-09.webp' },
  // 农业 · 蜂蜜原料同框（2026-10-11，客户真实产品照）。
  // 与 case-07 同属农业蜂产品，但**样本形态不同**：这条是「成品 + 原料（蜂巢）同框」，
  // 卖点落在"看得见真材实料"，与三瓶排开的净底图是两种解法。
  // 原图仅 350×350 —— 但卡片实际显示尺寸（220–275px 宽）根本不需要放大，
  // 所以按**原生像素**补白到 3:4 落盘（不放大就不会糊），而不是硬拉到 900×1200。
  { id: 'case-10', name: '农业 · 蜂蜜原料同框', description: '示例成品：成品与原料（蜂巢）同框，让买家一眼看见「真材实料」', category: 'CASE_HOT', to: '/detail-page?mode=main', cover: '/templates/case-10.webp' },
  // 卫浴洁具 · 场景图（2026-10-11，客户真实产品照）。
  // 这是案例里**唯一一张「场景图」**（其余多为净底 / 摆开）：产品长在真实卫浴空间里，
  // 卖点落在「装上之后是什么样」，比净底图更能让买家代入 —— 与 case-08 的净底图为两种解法。
  //
  // ⚠️ 原图右下角带**权利人「BOKSAS 原创设计 盗图必究」水印**，作者已授权使用。
  // 落盘时**不裁、不抹、不修水印**：原图 1200×1200 若要裁成 3:4 必须切掉左右两侧
  // （会切到产品与这枚水印），所以改为**上下补白**保完整。后续若拿到无水印版再替换。
  { id: 'case-11', name: '卫浴洁具 · 场景图', description: '示例成品：产品放进真实卫浴空间实拍，让买家先看见「装上之后长什么样」', category: 'CASE_HOT', to: '/detail-page?mode=main', cover: '/templates/case-11.webp' },
]

/**
 * 按分类取模板 —— 列表页与首页「热门同款」共用，两边都不写第二份 filter。
 *
 * ## 第三个参数 `custom` 是什么（2026-10-08 新增）
 *
 * 用户自己存的视频模板（`video_templates` 表，按账号、异步拉取）。
 * 它们**不写进 `TEMPLATES`** —— 那份是随版本发布的代码资产，而自存模板
 * 是运行期数据；混在一起就等于「用户数据写进了源码」。
 *
 * 传进来的是**已经映射成 `TemplateItem` 的数组**，不是 `CustomVideoTemplate`：
 * 映射要用到 `videoTemplates.ts` 的 `templateTitle()`，而本文件一旦 import 它
 * 就成环了（`videoTemplates → prompts → catalog → videoTemplates`）。
 * 所以由调用方（`components/TemplateGallery.tsx`，它本来就在最上层）先映射好。
 *
 * ## 顺序：自存的排在最前
 *
 * 与工作台 `templatesForMode()` 是同一个口径 —— 用户刚点完「存为模板」，
 * 回到模板中心第一眼就该看到自己那条，而不是滚过二十多张预设卡。
 */
export function templatesOfCategory(
  cat: TemplateCategoryId | 'ALL' | 'FAVORITES',
  favs: string[] = [],
  custom: TemplateItem[] = [],
): TemplateItem[] {
  const all = [...custom, ...TEMPLATES]
  if (cat === 'ALL') return all
  /*
   * 收藏也一起过 —— 收藏键在自存模板卡上同样渲染（本地存 id）。
   * 只 filter `TEMPLATES` 的话，用户收藏了自己的模板、切到「我的收藏」
   * 却是空的，看着像收藏没生效。
   */
  if (cat === 'FAVORITES') return all.filter((t) => favs.includes(t.id))
  return all.filter((t) => t.category === cat)
}

/* -------------------------------------------------------------------------- */
/* 平台预估耗时（参考站客户端策略，用于进度公式）                              */
/* -------------------------------------------------------------------------- */

export const ESTIMATED_MS = {
  image: 45000,
  videoAudio3d: 240000,
  livestream: 300000,
  other: 90000,
}

/** 参考站进度公式：percent = 95 * (1 - exp(-3t/estimatedMs)) */
export function progressFrom(elapsedMs: number, estimatedMs: number): number {
  const pct = 95 * (1 - Math.exp((-3 * elapsedMs) / Math.max(estimatedMs, 1)))
  return Math.min(99, Math.max(0, pct))
}

/**
 * 语音合成的预估耗时（毫秒）—— **它是公式，不是常量**。
 *
 * 依据：直连官方实测约 130 字/秒（945 字 7.4 秒），再留 3 秒的提交与落盘开销。
 * 写成常量的话，一段 20 字的稿子会挂着一条「预计 20 秒」的进度条，
 * 而它 1.4 秒就跑完了 —— 用户盯着那条永远爬不完的进度以为卡住了。
 *
 * 🔴 **它必须只有一处**（2026-10-09 提到这里）：语音合成有**两个发起页**
 * —— `/tts` 的「生成语音」与数字人页的「生成配音」，两处各写一份的话，
 * 改了一边另一边就静默漂开，而症状只是「这个页面的进度条不准」，
 * 没人会去比对两份公式。后端另有一条兜底（`_default_estimate('audio')` = 20 秒），
 * 只在页面没传时生效。
 */
export const estimateAudioMs = (chars: number) => 3000 + Math.ceil(chars / 130) * 1000
